/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import "./WaitlistModal.css";
import CMS from "../cms.json";

// --- Shared constant for the release date ---
const RELEASE_TARGET_DATE = CMS["release-data"]["release-date"];
const RELEASE_TITLE = CMS["release-data"]["release-title"];
const RELEASE_INFO = CMS["release-data"]["release-info"];
const RELEASE_ART = CMS["release-data"]["release-art"];
const RELEASE_TYPE = CMS["release-data"]["release-type"];

const Countdown = ({ targetDate }) => {
  const calculateTimeLeft = React.useCallback(() => {
    const difference = +new Date(targetDate) - +new Date();
    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }
    return timeLeft;
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate, calculateTimeLeft]);

  const timerComponents = [];
  const timePeriods = ["days", "hours", "minutes", "seconds"];

  timePeriods.forEach((interval) => {
    if (timeLeft[interval] !== undefined) {
      const value = timeLeft[interval].toString().padStart(2, "0");
      timerComponents.push(
        <div className="countdown-item" key={interval}>
          <div className="countdown-value">{value}</div>
          <div className="countdown-label">{interval}</div>
        </div>
      );
    }
  });

  return (
    <div className="countdown-container">
      {timerComponents.length ? (
        <div className="countdown">{timerComponents}</div>
      ) : (
        <span>Time's up!</span>
      )}
    </div>
  );
};

const WaitlistModal = ({ show, onClose }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");

  const isReleased = new Date() > new Date(RELEASE_TARGET_DATE);

  useEffect(() => {
    if (show) {
      const setVhProperty = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
      };
      setVhProperty();
      window.addEventListener("resize", setVhProperty);
      document.body.style.overflow = "hidden";

      setIsSubmitted(false);
      setError("");
      setEmail("");

      return () => {
        window.removeEventListener("resize", setVhProperty);
        document.body.style.overflow = "";
      };
    }
  }, [show]);

  if (!show) {
    return null;
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch(
        "https://the-creaks-worker.hubertdoubek12.workers.dev/waitlist-songs",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || "An error occurred. Please try again.");
      }
    } catch (error) {
      setError("Network error. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>
          ×
        </button>

        <div className="modal-visual-panel">
          <div className="scrolling-artwork-container">
            <img src={RELEASE_ART} alt="" className="scrolling-art" />
            <img src={RELEASE_ART} alt="" className="scrolling-art" />
            <img src={RELEASE_ART} alt="" className="scrolling-art" />
            <img src={RELEASE_ART} alt="" className="scrolling-art" />
          </div>
        </div>

        <img
          src={RELEASE_ART}
          alt="Album Art"
          className="modal-artwork-mobile"
        />

        <div className="modal-main-panel">
          <div className="modal-title-section">
            <h2 className="modal-title">{RELEASE_TITLE}</h2>
            {RELEASE_TYPE && <p className="release-type">{RELEASE_TYPE}</p>}

            <p className="modal-info">
              {RELEASE_INFO}
              <a
                href="#releases"
                className="read-more-subtle"
                onClick={onClose}
              >
                Read more
              </a>
            </p>
          </div>

          {isReleased ? (
            // --- "Out Now" View (Post-Release) ---
            <div className="released-view">
              <h3 className="released-title">Out Now!</h3>
              <p className="released-prompt">
                Listen on your favorite platform.
              </p>
              <div className="released-actions-container">
                <a
                  href={CMS["release-data"]["spotify-link"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="platform-button spotify-button-released"
                  aria-label="Listen on Spotify"
                >
                  <img
                    src="https://img.icons8.com/?size=100&id=11116&format=png&color=FFFFFF"
                    alt="Spotify Logo"
                    className="platform-icon"
                  />
                  <span>Listen on Spotify</span>
                </a>
                <a
                  href={CMS["release-data"]["apple-music-link"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="platform-button apple-music-button-released"
                  aria-label="Listen on Apple Music"
                >
                  <img
                    src="https://img.icons8.com/?size=100&id=mpeojql23sni&format=png&color=000000"
                    alt="Apple Music Logo"
                    className="platform-icon"
                  />
                  <span>Listen on Apple Music</span>
                </a>
                <a
                  href={CMS["release-data"]["ytb-link"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="platform-button youtube-button-released"
                  aria-label="Watch on YouTube"
                >
                  <img
                    src="https://img.icons8.com/?size=100&id=Mw6P3tmWMfOB&format=png&color=FFFFFF"
                    alt="YouTube Logo"
                    className="platform-icon"
                  />
                  <span>Watch on YouTube</span>
                </a>
                <a
                  href={CMS["release-data"]["tidal-link"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="platform-button tidal-button-released"
                  aria-label="Listen on Tidal"
                >
                  <img
                    src="https://img.icons8.com/?size=100&id=C1DUEYn7PMsS&format=png&color=FFFFFF"
                    alt="Tidal Logo"
                    className="platform-icon"
                  />
                  <span>Listen on Tidal</span>
                </a>
              </div>
              <p className="and-more">
                <a
                  href={CMS["release-data"]["hyperfollow-link"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="read-more-subtle"
                >
                  See all platforms
                </a>
              </p>
            </div>
          ) : (
            // --- Waitlist/Countdown View (Pre-Release) ---
            <>
              <Countdown targetDate={RELEASE_TARGET_DATE} />
              {isSubmitted ? (
                <div className="success-view">
                  <div className="success-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                      <circle
                        className="success-icon__circle"
                        cx="26"
                        cy="26"
                        r="25"
                        fill="none"
                      />
                      <path
                        className="success-icon__check"
                        fill="none"
                        d="M14.1 27.2l7.1 7.2 16.7-16.8"
                      />
                    </svg>
                  </div>
                  <h3 className="success-title">You're on the list!</h3>
                  <p className="success-prompt">
                    Next, pre-save the song on Spotify to be the first to hear
                    it on release day.
                  </p>
                  <button
                    onClick={() =>
                      window.open(
                        CMS["release-data"]["hyperfollow-link"],
                        "_blank"
                      )
                    }
                    className="spotify-button spotify-button-primary"
                  >
                    Pre-Save on Spotify
                  </button>
                </div>
              ) : (
                <>
                  <div className="modal-body">
                    <p className="join-prompt">
                      Be the first to hear it. We will send you a customized
                      email once the song is ready!
                    </p>
                    <form onSubmit={handleFormSubmit}>
                      <input
                        type="email"
                        placeholder="Enter your email"
                        className="email-input"
                        required
                        disabled={isSubmitting}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Joining..." : "Join Waitlist"}
                      </button>
                      {error && <p className="error-message">{error}</p>}
                    </form>
                  </div>
                  <div className="modal-footer">
                    <p className="spotify-cta">or</p>
                    <button
                      className="spotify-button"
                      onClick={() =>
                        window.open(
                          CMS["release-data"]["hyperfollow-link"],
                          "_blank"
                        )
                      }
                    >
                      Pre-Save on Spotify
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WaitlistModal;